import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";

const CampaignCategoryModal = ({
  modal_center,
  tog_center,
  category,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    arabic_name: "",
    icon: "",
    is_active: true,
  });

  const [iconType, setIconType] = useState("remixicon"); // remixicon, fontawesome, custom, upload
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState("");

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || "",
        arabic_name: category.arabic_name || "",
        icon: category.icon || "",
        is_active: category.is_active === 1,
      });
      // Detect icon type
      if (category.icon) {
        if (category.icon.startsWith("fa")) {
          setIconType("fontawesome");
        } else if (
          category.icon.startsWith("http") ||
          category.icon.includes(".")
        ) {
          setIconType("custom");
        } else {
          setIconType("remixicon");
        }
      }
    } else {
      setFormData({
        name: "",
        arabic_name: "",
        icon: "",
        is_active: true,
      });
      setIconType("remixicon");
    }
  }, [category]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type (PNG only)
      if (file.type !== "image/png") {
        alert("Please upload a PNG image file");
        return;
      }

      // Validate file size (max 1MB)
      if (file.size > 1 * 1024 * 1024) {
        alert("File size must be less than 1MB");
        return;
      }

      setUploadedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadPreview(reader.result);
        setFormData({ ...formData, icon: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.name.trim()) {
      alert("Please enter a name");
      return;
    }

    if (!formData.arabic_name.trim()) {
      alert("Please enter an Arabic name");
      return;
    }

    // Convert is_active to 1 or 0
    const dataToSend = {
      ...formData,
      is_active: formData.is_active ? 1 : 0,
    };

    onSave(dataToSend);
  };

  // Common icon options for campaigns
  const remixIconOptions = [
    { value: "", label: "No Icon" },
    // Marketing & Advertising
    { value: "ri-megaphone-line", label: "Megaphone" },
    { value: "ri-advertisement-line", label: "Advertisement" },
    { value: "ri-speaker-2-line", label: "Speaker" },
    { value: "ri-bullhorn-line", label: "Bullhorn" },
    { value: "ri-broadcast-line", label: "Broadcast" },
    { value: "ri-radar-line", label: "Radar" },

    // Rewards & Achievement
    { value: "ri-star-line", label: "Star" },
    { value: "ri-star-fill", label: "Star Filled" },
    { value: "ri-vip-crown-line", label: "VIP Crown" },
    { value: "ri-medal-line", label: "Medal" },
    { value: "ri-trophy-line", label: "Trophy" },
    { value: "ri-award-line", label: "Award" },

    // Energy & Action
    { value: "ri-fire-line", label: "Fire" },
    { value: "ri-rocket-line", label: "Rocket" },
    { value: "ri-flashlight-line", label: "Flash" },
    { value: "ri-lightbulb-line", label: "Lightbulb" },
    { value: "ri-flashlight-fill", label: "Lightning" },
    { value: "ri-magic-line", label: "Magic" },

    // Shopping & Commerce
    { value: "ri-gift-line", label: "Gift" },
    { value: "ri-shopping-bag-line", label: "Shopping Bag" },
    { value: "ri-shopping-cart-line", label: "Shopping Cart" },
    { value: "ri-price-tag-3-line", label: "Price Tag" },
    { value: "ri-coupon-line", label: "Coupon" },
    { value: "ri-wallet-line", label: "Wallet" },
    { value: "ri-bank-card-line", label: "Credit Card" },
    { value: "ri-money-dollar-circle-line", label: "Dollar" },

    // Engagement & Social
    { value: "ri-thumb-up-line", label: "Thumb Up" },
    { value: "ri-heart-line", label: "Heart" },
    { value: "ri-heart-fill", label: "Heart Filled" },
    { value: "ri-emotion-happy-line", label: "Happy Face" },
    { value: "ri-chat-smile-3-line", label: "Chat Smile" },
    { value: "ri-customer-service-2-line", label: "Support" },

    // Communication
    { value: "ri-notification-line", label: "Notification" },
    { value: "ri-message-2-line", label: "Message" },
    { value: "ri-mail-send-line", label: "Mail Send" },
    { value: "ri-share-line", label: "Share" },
    { value: "ri-send-plane-line", label: "Send Plane" },

    // Technology & Digital
    { value: "ri-smartphone-line", label: "Smartphone" },
    { value: "ri-global-line", label: "Global" },
    { value: "ri-wifi-line", label: "WiFi" },
    { value: "ri-qr-code-line", label: "QR Code" },
    { value: "ri-camera-line", label: "Camera" },

    // Business & Growth
    { value: "ri-line-chart-line", label: "Chart Up" },
    { value: "ri-stock-line", label: "Stock" },
    { value: "ri-funds-line", label: "Funds" },
    { value: "ri-bar-chart-line", label: "Bar Chart" },
    { value: "ri-pie-chart-line", label: "Pie Chart" },

    // Special & Seasonal
    { value: "ri-cake-line", label: "Birthday" },
    { value: "ri-balloon-line", label: "Balloon" },
    { value: "ri-music-line", label: "Music" },
    { value: "ri-video-line", label: "Video" },
    { value: "ri-movie-line", label: "Movie" },

    // Location & Local
    { value: "ri-map-pin-line", label: "Location" },
    { value: "ri-store-line", label: "Store" },
    { value: "ri-home-line", label: "Home" },
    { value: "ri-building-line", label: "Building" },

    // Time & Events
    { value: "ri-calendar-event-line", label: "Event" },
    { value: "ri-time-line", label: "Time" },
    { value: "ri-alarm-line", label: "Alarm" },

    // Food & Dining
    { value: "ri-restaurant-line", label: "Restaurant" },
    { value: "ri-cup-line", label: "Cup" },
    { value: "ri-cake-2-line", label: "Cake" },

    // Travel & Transportation
    { value: "ri-car-line", label: "Car" },
    { value: "ri-flight-takeoff-line", label: "Flight" },
    { value: "ri-ship-line", label: "Ship" },

    // Health & Fitness
    { value: "ri-heart-pulse-line", label: "Health" },
    { value: "ri-run-line", label: "Running" },
    { value: "ri-bike-line", label: "Bike" },
  ];

  const fontAwesomeIconOptions = [
    { value: "", label: "No Icon" },
    // Marketing & Advertising
    { value: "fas fa-bullhorn", label: "Bullhorn" },
    { value: "fas fa-ad", label: "Advertisement" },
    { value: "fas fa-broadcast-tower", label: "Broadcast" },

    // Rewards & Achievement
    { value: "fas fa-star", label: "Star" },
    { value: "fas fa-crown", label: "Crown" },
    { value: "fas fa-medal", label: "Medal" },
    { value: "fas fa-trophy", label: "Trophy" },
    { value: "fas fa-award", label: "Award" },
    { value: "fas fa-certificate", label: "Certificate" },

    // Energy & Action
    { value: "fas fa-fire", label: "Fire" },
    { value: "fas fa-fire-alt", label: "Fire Alt" },
    { value: "fas fa-rocket", label: "Rocket" },
    { value: "fas fa-bolt", label: "Lightning" },
    { value: "fas fa-lightbulb", label: "Lightbulb" },
    { value: "fas fa-magic", label: "Magic" },
    { value: "fas fa-wand-magic-sparkles", label: "Sparkles" },

    // Shopping & Commerce
    { value: "fas fa-gift", label: "Gift" },
    { value: "fas fa-shopping-bag", label: "Shopping Bag" },
    { value: "fas fa-shopping-cart", label: "Shopping Cart" },
    { value: "fas fa-shopping-basket", label: "Basket" },
    { value: "fas fa-tag", label: "Tag" },
    { value: "fas fa-tags", label: "Tags" },
    { value: "fas fa-barcode", label: "Barcode" },
    { value: "fas fa-wallet", label: "Wallet" },
    { value: "fas fa-credit-card", label: "Credit Card" },
    { value: "fas fa-money-bill-wave", label: "Money" },
    { value: "fas fa-dollar-sign", label: "Dollar" },
    { value: "fas fa-coins", label: "Coins" },
    { value: "fas fa-percentage", label: "Percentage" },

    // Engagement & Social
    { value: "fas fa-thumbs-up", label: "Thumbs Up" },
    { value: "fas fa-heart", label: "Heart" },
    { value: "fas fa-smile", label: "Smile" },
    { value: "fas fa-handshake", label: "Handshake" },
    { value: "fas fa-users", label: "Users" },
    { value: "fas fa-user-friends", label: "Friends" },
    { value: "fas fa-comments", label: "Comments" },

    // Communication
    { value: "fas fa-bell", label: "Notification" },
    { value: "fas fa-envelope", label: "Mail" },
    { value: "fas fa-paper-plane", label: "Send" },
    { value: "fas fa-share-alt", label: "Share" },
    { value: "fas fa-share-nodes", label: "Share Network" },
    { value: "fas fa-comment", label: "Comment" },
    { value: "fas fa-comment-dots", label: "Chat" },

    // Technology & Digital
    { value: "fas fa-mobile-alt", label: "Mobile" },
    { value: "fas fa-laptop", label: "Laptop" },
    { value: "fas fa-desktop", label: "Desktop" },
    { value: "fas fa-globe", label: "Globe" },
    { value: "fas fa-wifi", label: "WiFi" },
    { value: "fas fa-qrcode", label: "QR Code" },
    { value: "fas fa-camera", label: "Camera" },
    { value: "fas fa-video", label: "Video" },

    // Business & Growth
    { value: "fas fa-chart-line", label: "Chart Up" },
    { value: "fas fa-chart-bar", label: "Bar Chart" },
    { value: "fas fa-chart-pie", label: "Pie Chart" },
    { value: "fas fa-bullseye", label: "Target" },
    { value: "fas fa-briefcase", label: "Briefcase" },
    { value: "fas fa-business-time", label: "Business" },

    // Special & Seasonal
    { value: "fas fa-birthday-cake", label: "Birthday" },
    { value: "fas fa-music", label: "Music" },
    { value: "fas fa-film", label: "Film" },
    { value: "fas fa-gamepad", label: "Game" },
    { value: "fas fa-gifts", label: "Gifts" },
    { value: "fas fa-glass-cheers", label: "Celebrate" },

    // Location & Local
    { value: "fas fa-map-marker-alt", label: "Location" },
    { value: "fas fa-map-pin", label: "Pin" },
    { value: "fas fa-store", label: "Store" },
    { value: "fas fa-home", label: "Home" },
    { value: "fas fa-building", label: "Building" },
    { value: "fas fa-warehouse", label: "Warehouse" },

    // Time & Events
    { value: "fas fa-calendar", label: "Calendar" },
    { value: "fas fa-calendar-check", label: "Event" },
    { value: "fas fa-clock", label: "Clock" },
    { value: "fas fa-hourglass-half", label: "Hourglass" },

    // Food & Dining
    { value: "fas fa-utensils", label: "Restaurant" },
    { value: "fas fa-coffee", label: "Coffee" },
    { value: "fas fa-pizza-slice", label: "Pizza" },
    { value: "fas fa-hamburger", label: "Burger" },
    { value: "fas fa-ice-cream", label: "Ice Cream" },

    // Travel & Transportation
    { value: "fas fa-car", label: "Car" },
    { value: "fas fa-plane", label: "Plane" },
    { value: "fas fa-train", label: "Train" },
    { value: "fas fa-bus", label: "Bus" },
    { value: "fas fa-ship", label: "Ship" },
    { value: "fas fa-bicycle", label: "Bicycle" },

    // Health & Fitness
    { value: "fas fa-heartbeat", label: "Health" },
    { value: "fas fa-running", label: "Running" },
    { value: "fas fa-dumbbell", label: "Fitness" },
    { value: "fas fa-spa", label: "Spa" },
  ];

  const getIconOptions = () => {
    if (iconType === "fontawesome") return fontAwesomeIconOptions;
    if (iconType === "remixicon") return remixIconOptions;
    return [];
  };

  return (
    <Modal
      isOpen={modal_center}
      toggle={tog_center}
      centered
      size="lg"
      style={{ maxWidth: "900px" }}
    >
      <ModalHeader toggle={tog_center}>
        {category ? "Edit Campaign Category" : "Add Campaign Category"}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label for="name">
                  Name (English) <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter category name in English"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label for="arabic_name">
                  Name (Arabic) <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="arabic_name"
                  name="arabic_name"
                  placeholder="أدخل اسم الفئة بالعربية"
                  value={formData.arabic_name}
                  onChange={handleChange}
                  required
                  dir="rtl"
                  style={{ textAlign: "right" }}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label for="iconType">Icon Type</Label>
                <Input
                  type="select"
                  id="iconType"
                  name="iconType"
                  value={iconType}
                  onChange={(e) => {
                    setIconType(e.target.value);
                    if (e.target.value === "custom") {
                      setFormData({ ...formData, icon: "" });
                    }
                  }}
                >
                  <option value="remixicon">Remix Icon</option>
                  <option value="fontawesome">Font Awesome</option>
                  <option value="custom">Custom (URL)</option>
                  <option value="upload">Upload SVG/Image</option>
                </Input>
                <small className="text-muted">
                  Choose the type of icon you want to use
                </small>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label for="icon">
                  {iconType === "custom"
                    ? "Icon URL"
                    : iconType === "upload"
                    ? "Upload Icon"
                    : "Select Icon"}
                </Label>
                {iconType === "upload" ? (
                  <div>
                    <Input
                      type="file"
                      id="iconFile"
                      name="iconFile"
                      accept="image/png"
                      onChange={handleFileUpload}
                      className="mb-2"
                    />
                    {uploadPreview && (
                      <div className="mt-2">
                        <div
                          className="border rounded p-3 d-inline-flex"
                          style={{
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          <img
                            src={uploadPreview}
                            alt="Uploaded icon preview"
                            style={{
                              width: "60px",
                              height: "60px",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                        <div className="mt-2">
                          <small className="text-success d-block">
                            <i className="ri-check-line"></i> File uploaded
                            successfully
                          </small>
                          <small className="text-muted d-block">
                            {uploadedFile?.name} (
                            {(uploadedFile?.size / 1024).toFixed(2)} KB)
                          </small>
                        </div>
                      </div>
                    )}
                    <small className="text-muted d-block mt-2">
                      Upload SVG, PNG, or JPG (max 2MB)
                    </small>
                  </div>
                ) : iconType === "custom" ? (
                  <div>
                    <Input
                      type="text"
                      id="icon"
                      name="icon"
                      placeholder="Enter image URL (e.g., https://example.com/icon.svg)"
                      value={formData.icon}
                      onChange={handleChange}
                    />
                    {formData.icon && (
                      <div className="mt-2">
                        <div
                          className="border rounded p-2 d-inline-flex"
                          style={{
                            width: "40px",
                            height: "40px",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={formData.icon}
                            alt="Icon preview"
                            style={{ maxWidth: "100%", maxHeight: "100%" }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "block";
                            }}
                          />
                          <span
                            style={{ display: "none", fontSize: "10px" }}
                            className="text-danger"
                          >
                            Invalid
                          </span>
                        </div>
                        <small className="text-muted d-block">
                          Icon Preview
                        </small>
                      </div>
                    )}
                    <small className="text-muted d-block mt-2">
                      Enter the full URL to your icon image
                    </small>
                  </div>
                ) : (
                  <div>
                    {/* Selected Icon Preview */}
                    {formData.icon && (
                      <div
                        className="mb-3 text-center p-3 border rounded"
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        <div
                          className="d-inline-flex border rounded p-3"
                          style={{
                            width: "80px",
                            height: "80px",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "white",
                          }}
                        >
                          <i
                            className={`${formData.icon} fs-1`}
                            style={{ color: "#0d6efd" }}
                          ></i>
                        </div>
                        <div className="mt-2">
                          <small className="text-success d-block fw-bold">
                            <i className="ri-check-line"></i> Icon Selected
                          </small>
                        </div>
                      </div>
                    )}

                    {/* Icon Grid */}
                    <div
                      className="p-3 border rounded"
                      style={{
                        backgroundColor: "#f8f9fa",
                        maxHeight: "500px",
                        overflowY: "auto",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <small className="text-muted fw-bold">
                          {formData.icon
                            ? "Selected icon highlighted below:"
                            : "Click to select an icon:"}
                        </small>
                        <small className="badge bg-primary">
                          {
                            getIconOptions().filter((opt) => opt.value !== "")
                              .length
                          }{" "}
                          icons available
                        </small>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(70px, 1fr))",
                          gap: "10px",
                        }}
                      >
                        {getIconOptions()
                          .filter((opt) => opt.value !== "")
                          .map((option) => (
                            <div
                              key={option.value}
                              onClick={() =>
                                setFormData({ ...formData, icon: option.value })
                              }
                              style={{
                                padding: "14px 8px",
                                border:
                                  formData.icon === option.value
                                    ? "2px solid #0d6efd"
                                    : "1px solid #dee2e6",
                                borderRadius: "8px",
                                cursor: "pointer",
                                textAlign: "center",
                                backgroundColor:
                                  formData.icon === option.value
                                    ? "#e7f1ff"
                                    : "white",
                                transition: "all 0.2s",
                              }}
                              title={option.label}
                              onMouseEnter={(e) => {
                                if (formData.icon !== option.value) {
                                  e.currentTarget.style.backgroundColor =
                                    "#f8f9fa";
                                  e.currentTarget.style.borderColor = "#0d6efd";
                                  e.currentTarget.style.transform =
                                    "scale(1.05)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (formData.icon !== option.value) {
                                  e.currentTarget.style.backgroundColor =
                                    "white";
                                  e.currentTarget.style.borderColor = "#dee2e6";
                                  e.currentTarget.style.transform = "scale(1)";
                                }
                              }}
                            >
                              <i
                                className={`${option.value} d-block mb-2`}
                                style={{ fontSize: "28px" }}
                              ></i>
                              <small
                                style={{
                                  fontSize: "10px",
                                  display: "block",
                                  lineHeight: "1.2",
                                  wordBreak: "break-word",
                                }}
                              >
                                {option.label}
                              </small>
                            </div>
                          ))}
                      </div>
                    </div>
                    <small className="text-muted d-block mt-2">
                      Click any icon to select it for your category
                    </small>
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup check>
                <Input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <Label check for="is_active">
                  Active
                </Label>
                <small className="d-block text-muted">
                  When active, this category will be visible to users
                </small>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={tog_center}>
            Cancel
          </Button>
          <Button color="primary" type="submit">
            {category ? "Update" : "Create"} Category
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CampaignCategoryModal;
